import React, { useState, useCallback } from 'react';
import { Eraser, Sparkles, Pipette, Sliders, Check, RefreshCw, Layers, ShieldCheck, Paintbrush } from 'lucide-react';
import { BackgroundBrushModal } from './BackgroundBrushModal';

interface BackgroundRemovalProps {
  originalImage: File;
  currentProcessedUrl?: string | null;
  onApplyBackgroundRemoval: (processedDataUrl: string) => void;
}

export function BackgroundRemoval({
  originalImage,
  currentProcessedUrl,
  onApplyBackgroundRemoval,
}: BackgroundRemovalProps) {
  const [selectedColor, setSelectedColor] = useState<string>('#ffffff');
  const [tolerance, setTolerance] = useState<number>(30);
  const [feather, setFeather] = useState<number>(2);
  const [bgType, setBgType] = useState<'transparent' | 'color' | 'gradient'>('transparent');
  const [replacementColor, setReplacementColor] = useState<string>('#4f46e5');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [showBrushModal, setShowBrushModal] = useState<boolean>(false);

  // Perform client-side color keying background removal
  const processRemoval = useCallback(() => {
    setIsProcessing(true);
    const img = new Image();
    const url = URL.createObjectURL(originalImage);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        setIsProcessing(false);
        URL.revokeObjectURL(url);
        return;
      }

      // Draw original image
      ctx.drawImage(img, 0, 0);
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;

      // Convert target hex color to RGB
      const hex = selectedColor.replace('#', '');
      const targetR = parseInt(hex.substring(0, 2), 16) || 255;
      const targetG = parseInt(hex.substring(2, 4), 16) || 255;
      const targetB = parseInt(hex.substring(4, 6), 16) || 255;

      const maxDiff = (tolerance / 100) * 441.67; // Max Euclidean distance in RGB color space (sqrt(255^2*3))

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Euclidean color distance
        const diff = Math.sqrt(
          Math.pow(r - targetR, 2) + Math.pow(g - targetG, 2) + Math.pow(b - targetB, 2)
        );

        if (diff <= maxDiff) {
          // Calculate alpha transparency gradient (feathering)
          if (feather > 0 && diff > maxDiff - feather * 10) {
            const alphaFactor = (diff - (maxDiff - feather * 10)) / (feather * 10);
            data[i + 3] = Math.round(alphaFactor * 255);
          } else {
            data[i + 3] = 0; // Fully transparent
          }
        }
      }

      ctx.putImageData(imgData, 0, 0);

      // Replace background if color or gradient is selected
      if (bgType !== 'transparent') {
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = img.width;
        finalCanvas.height = img.height;
        const finalCtx = finalCanvas.getContext('2d');

        if (finalCtx) {
          if (bgType === 'color') {
            finalCtx.fillStyle = replacementColor;
            finalCtx.fillRect(0, 0, img.width, img.height);
          } else if (bgType === 'gradient') {
            const grad = finalCtx.createLinearGradient(0, 0, img.width, img.height);
            grad.addColorStop(0, '#4f46e5');
            grad.addColorStop(0.5, '#7c3aed');
            grad.addColorStop(1, '#ec4899');
            finalCtx.fillStyle = grad;
            finalCtx.fillRect(0, 0, img.width, img.height);
          }

          finalCtx.drawImage(canvas, 0, 0);
          const resultUrl = finalCanvas.toDataURL('image/png');
          onApplyBackgroundRemoval(resultUrl);
          setIsProcessing(false);
          URL.revokeObjectURL(url);
          return;
        }
      }

      const resultUrl = canvas.toDataURL('image/png');
      onApplyBackgroundRemoval(resultUrl);
      setIsProcessing(false);
      URL.revokeObjectURL(url);
    };

    img.src = url;
  }, [originalImage, selectedColor, tolerance, feather, bgType, replacementColor, onApplyBackgroundRemoval]);

  return (
    <div className="bg-slate-50 dark:bg-slate-950/60 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-800 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Eraser className="w-4 h-4 text-pink-500" />
          <span>Background Remover & Keyer</span>
        </h3>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pink-100 dark:bg-pink-950/60 text-pink-700 dark:text-pink-300 border border-pink-200 dark:border-pink-800">
          100% LOCAL
        </span>
      </div>

      {/* Target Color Preset Buttons */}
      <div className="space-y-2">
        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
          1. Select Background Color to Remove:
        </label>
        <div className="flex items-center gap-2">
          {[
            { label: 'White', color: '#ffffff' },
            { label: 'Black', color: '#000000' },
            { label: 'Green', color: '#00ff00' },
            { label: 'Blue', color: '#0000ff' },
          ].map((preset) => (
            <button
              key={preset.color}
              type="button"
              onClick={() => setSelectedColor(preset.color)}
              className={`flex-1 py-1.5 px-2 rounded-xl text-xs font-semibold border flex items-center justify-center gap-1.5 transition-all ${
                selectedColor.toLowerCase() === preset.color
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 ring-2 ring-indigo-500/30 font-bold'
                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:border-indigo-300'
              }`}
            >
              <span
                className="w-3.5 h-3.5 rounded-full border border-slate-300 shadow-sm"
                style={{ backgroundColor: preset.color }}
              />
              <span>{preset.label}</span>
            </button>
          ))}
          <input
            type="color"
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-800 cursor-pointer p-0.5 bg-white dark:bg-slate-900"
            title="Custom Pick Color"
          />
        </div>
      </div>

      {/* Color Tolerance & Feather Sliders */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
        <div className="space-y-1">
          <div className="flex justify-between text-xs font-semibold text-slate-700 dark:text-slate-300">
            <span>Color Tolerance</span>
            <span>{tolerance}%</span>
          </div>
          <input
            type="range"
            min="1"
            max="80"
            value={tolerance}
            onChange={(e) => setTolerance(Number(e.target.value))}
            className="w-full accent-indigo-600 cursor-pointer"
          />
        </div>

        <div className="space-y-1">
          <div className="flex justify-between text-xs font-semibold text-slate-700 dark:text-slate-300">
            <span>Edge Feathering</span>
            <span>{feather}px</span>
          </div>
          <input
            type="range"
            min="0"
            max="10"
            value={feather}
            onChange={(e) => setFeather(Number(e.target.value))}
            className="w-full accent-indigo-600 cursor-pointer"
          />
        </div>
      </div>

      {/* Replacement Background Selection */}
      <div className="space-y-2 pt-1 border-t border-slate-200/60 dark:border-slate-800">
        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
          2. Replacement Background:
        </label>
        <div className="grid grid-cols-3 gap-2">
          {(['transparent', 'color', 'gradient'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setBgType(type)}
              className={`py-1.5 px-2 rounded-xl text-xs font-bold capitalize border transition-all ${
                bgType === type
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                  : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-indigo-300'
              }`}
            >
              {type === 'transparent' ? '🏁 PNG Transparent' : type === 'color' ? '🎨 Custom Color' : '✨ Studio Gradient'}
            </button>
          ))}
        </div>

        {bgType === 'color' && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-slate-500 font-semibold">New Color:</span>
            <input
              type="color"
              value={replacementColor}
              onChange={(e) => setReplacementColor(e.target.value)}
              className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer"
            />
            <span className="text-xs font-mono text-slate-600 dark:text-slate-400">{replacementColor}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-2 pt-1">
        <button
          type="button"
          onClick={processRemoval}
          disabled={isProcessing}
          className="w-full py-2.5 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 hover:from-pink-700 hover:to-indigo-700 text-white font-bold text-xs rounded-xl shadow-md shadow-pink-500/20 flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
        >
          {isProcessing ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              <span>Removing Background...</span>
            </>
          ) : (
            <>
              <Eraser className="w-3.5 h-3.5" />
              <span>Remove Background ({selectedColor})</span>
            </>
          )}
        </button>

        {currentProcessedUrl && (
          <button
            type="button"
            onClick={() => setShowBrushModal(true)}
            className="w-full py-2 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 font-bold text-xs rounded-xl border border-emerald-300 dark:border-emerald-800 flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
          >
            <Paintbrush className="w-3.5 h-3.5" />
            <span>🖌️ Restore Eyes & Missing Details (Brush Tool)</span>
          </button>
        )}
      </div>

      {showBrushModal && currentProcessedUrl && (
        <BackgroundBrushModal
          originalImage={originalImage}
          currentProcessedUrl={currentProcessedUrl}
          onApply={onApplyBackgroundRemoval}
          onClose={() => setShowBrushModal(false)}
        />
      )}
    </div>
  );
}
